<script setup lang="ts">
import { computed, markRaw, nextTick, ref } from 'vue';
import { useRouter } from 'vue-router';
import * as Sentry from '@sentry/vue';
import CopyIcon from '@/assets/icons/shared/copy.svg';
import PlusIcon from '@/assets/icons/shared/plus.svg';
import CounterpartyCreatedMessage from '@/modules/core/components/notifications/message/success/CounterpartyCreatedMessage.vue';
import { Access } from '@/modules/common/enums/access.enum.ts';
import { notification } from '@/modules/common/composables/use-notification.ts';
import { ErrorUtil } from '@/modules/common/utils/error.util.ts';
import { Route } from '@/modules/core/enums/route.enum.ts';
import { useAccessStore } from '@/modules/core/stores/access.store.ts';
import CompletionIcon from '@/modules/counterparty/counterparty-master/components/CompletionIcon.vue';
import { useCounterpartyMaster } from '@/modules/counterparty/counterparty-master/counterparty-master.ts';
import { useCounterpartyMasterStepReady } from '@/modules/counterparty/counterparty-master/counterparty-master-step-ready.ts';
import { refreshCounterpartiesList } from '@/modules/counterparty/counterparty-master/utils/refresh-counterparties-list.util.ts';
import { CounterpartyApi } from '@/modules/shared/feature-bridges.ts';
import { resetDocumentMasterData, useDocumentMaster } from '@/modules/document/document-master/document-master.ts';
import { useMasterFormStore } from '@/modules/document/stores/master-form.ts';
import UiButton from '@/modules/ui/components/ui/UiButton.vue';
import UiTooltip from '@/modules/ui/components/ui/UiTooltip.vue';
import emitter, { EmitterEvent } from '@/emitter.ts';
import { useWizard, useWizardComplete, useWizardOptionalStep } from '@/providers/wizard.ts';

useWizardOptionalStep();

const router = useRouter();
const { createdId, isNested } = useCounterpartyMaster();
const { clear } = useWizard();
const accessStore = useAccessStore();
const documentMaster = useDocumentMaster();
const masterFormStore = useMasterFormStore();
const stepReady = useCounterpartyMasterStepReady();

const isOpeningDocument = ref(false);
const completionNotified = ref(false);
const openedAsNested = isNested.value;

const showCreateDocument = computed(() => accessStore.has(Access.DocumentsEdit) && !openedAsNested);
const canCreateDocument = computed(
  () => showCreateDocument.value && createdId.value !== null && !isOpeningDocument.value
);
const counterpartyHref = computed(() => {
  if (createdId.value === null) {
    return undefined;
  }

  return router.resolve({ name: Route.CounterpartyPage, params: { id: createdId.value } }).href;
});

function notifyCreated(id: number) {
  if (completionNotified.value || openedAsNested) {
    return;
  }

  completionNotified.value = true;
  notification.success({
    component: markRaw(CounterpartyCreatedMessage),
    props: { id },
  });
}

async function refreshCreated(id: number) {
  await refreshCounterpartiesList();

  if (openedAsNested) {
    return;
  }

  await nextTick();
  emitter.emit(EmitterEvent.CounterpartiesTableSelect, id);
}

useWizardComplete(async () => {
  if (isOpeningDocument.value) {
    return;
  }

  const id = createdId.value;

  try {
    if (id !== null) {
      notifyCreated(id);
      await refreshCreated(id);
    }
  } finally {
    if (!isOpeningDocument.value) {
      clear();
    }
  }
});

async function copy() {
  if (createdId.value === null) {
    return;
  }

  try {
    await navigator.clipboard.writeText(createdId.value.toString());
  } catch (error) {
    Sentry.captureException(error);
  }
}

async function createFirstDocument() {
  const id = createdId.value;

  if (id === null || !canCreateDocument.value) {
    return;
  }

  isOpeningDocument.value = true;

  try {
    const client = await CounterpartyApi.getClient(id);

    notifyCreated(id);
    await refreshCreated(id);

    resetDocumentMasterData();
    masterFormStore.setClients([client]);
    documentMaster.open();
    clear();
  } catch (error) {
    if (ErrorUtil.isUnexpected(error)) {
      notification.error('counterparty.notification.error.client.get', error);
    }
  } finally {
    isOpeningDocument.value = false;
  }
}
</script>

<template>
  <div class="step-completion">
    <header class="header">
      <div class="info">
        <span>{{ $t('counterparty.master.steps.completion.counterpartyNumber') }}:</span>

        <a
          v-if="createdId !== null"
          class="_dark-gray _hover _extra"
          :href="counterpartyHref"
          target="_blank"
          rel="noopener noreferrer"
        >
          {{ createdId }}
        </a>
      </div>

      <UiTooltip
        class="copy-button"
        text="counterparty.tooltip.copyCounterpartyNumber"
      >
        <UiButton
          mode="plain"
          color="dark-gray"
          shape="square"
          size="s"
          :disabled="createdId === null"
          @click="copy"
        >
          <CopyIcon />
        </UiButton>
      </UiTooltip>
    </header>

    <button
      v-if="showCreateDocument"
      type="button"
      class="create-document"
      :disabled="!canCreateDocument"
      @click="createFirstDocument"
    >
      <span class="create-document-label">
        {{ $t('counterparty.master.steps.completion.createFirstDocument') }}
      </span>

      <span class="create-document-icon">
        <PlusIcon />
      </span>
    </button>

    <div class="content">
      <CompletionIcon
        class="completion-icon"
        :play="stepReady"
      />
    </div>
  </div>
</template>

<style scoped lang="scss">
.step-completion {
  display: flex;
  height: 100%;
  min-width: 0;
  min-height: 0;
  flex-direction: column;
  gap: 16px;
}

.header,
.create-document {
  display: flex;
  flex-shrink: 0;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  min-width: 0;
  padding: 12px 16px;
  color: var(--color-gray-700);
  font-size: var(--font-size-xl);
  line-height: var(--line-height-xl);
  background-color: var(--color-gray-075);
  border-radius: 8px;
}

.create-document {
  width: 100%;
  border: 0;
  text-align: left;
  cursor: pointer;
  transition:
    background-color var(--transition-fast),
    box-shadow var(--transition-fast);

  &:disabled {
    cursor: default;
    opacity: 0.6;
  }

  @media (hover: hover) {
    &:not(:disabled):hover {
      background-color: var(--color-gray-100);
      box-shadow: 0 0 8px 0 var(--color-gray-300);
    }
  }
}

.create-document-label {
  min-width: 0;
}

.create-document-icon {
  display: flex;
  flex-shrink: 0;
  width: 24px;
  height: 24px;
  align-items: center;
  justify-content: center;
  color: var(--color-gray-600);

  :deep(svg) {
    width: 16px;
    height: 16px;
    fill: currentcolor;
  }
}

.info {
  display: flex;
  min-width: 0;
  align-items: center;
  flex-wrap: wrap;
  gap: 8px;
}

.copy-button {
  flex-shrink: 0;
}

.content {
  display: flex;
  flex-grow: 1;
  min-width: 0;
  min-height: 0;
  align-items: center;
  justify-content: center;
}

.completion-icon {
  width: 120px;
  height: 120px;
}
</style>
